import { useState } from 'react';
import { formaterDuree } from '../format.js';
import { COULEURS, ESPACEMENTS, styleBase, styleParagraphe, type Variante } from '../tokens/tokens.js';
import { styleBoutonSecondaire } from './styles.js';

export type TypePiece = 'photo' | 'video' | 'pdf';

export interface ApercuPieceProps {
  type: TypePiece;
  src: string;
  titre: string;
  /** Vidéo : durée affichée en toutes lettres. */
  dureeSecondes?: number;
  /** Vidéo : fichier de sous-titres (WebVTT). */
  sousTitres?: string;
  /** PDF : page affichée (1 par défaut) et nombre de pages. */
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  variante?: Variante;
}

export function ApercuPiece({
  type,
  src,
  titre,
  dureeSecondes,
  sousTitres,
  page,
  totalPages,
  onPageChange,
  variante = 'equipe',
}: ApercuPieceProps) {
  const [pageInterne, setPageInterne] = useState(1);
  const courante = page ?? pageInterne;
  const aller = (cible: number) => {
    if (page === undefined) setPageInterne(cible);
    onPageChange?.(cible);
  };
  const cadre = {
    width: '100%',
    maxWidth: 560,
    border: `1px solid ${COULEURS.bordure}`,
    borderRadius: 8,
    backgroundColor: COULEURS.fondDoux,
  } as const;

  return (
    <figure style={{ ...styleBase(variante), margin: 0, display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.s }}>
      {type === 'photo' ? <img src={src} alt={titre} style={{ ...cadre, height: 'auto' }} /> : null}
      {type === 'video' ? (
        <>
          <video src={src} controls preload="metadata" aria-label={titre} style={{ ...cadre, height: 'auto' }}>
            {sousTitres ? <track kind="captions" src={sousTitres} srcLang="fr" label="Français" default /> : null}
          </video>
          {dureeSecondes !== undefined ? <p style={styleParagraphe(variante)}>{`Durée : ${formaterDuree(dureeSecondes)}`}</p> : null}
          {sousTitres ? null : (
            <p style={{ ...styleParagraphe(variante), color: COULEURS.texteSecondaire }}>Sous-titres indisponibles pour cette vidéo.</p>
          )}
        </>
      ) : null}
      {type === 'pdf' ? (
        <>
          <iframe src={`${src}#page=${courante}`} title={titre} style={{ ...cadre, height: 420 }} />
          {totalPages !== undefined ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: ESPACEMENTS.s, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={courante <= 1}
                onClick={() => aller(courante - 1)}
                style={{ ...styleBoutonSecondaire(variante), opacity: courante <= 1 ? 0.6 : 1 }}
              >
                Page précédente
              </button>
              <p aria-live="polite" style={{ ...styleParagraphe(variante), fontWeight: 600 }}>{`Page ${courante} / ${totalPages}`}</p>
              <button
                type="button"
                disabled={courante >= totalPages}
                onClick={() => aller(courante + 1)}
                style={{ ...styleBoutonSecondaire(variante), opacity: courante >= totalPages ? 0.6 : 1 }}
              >
                Page suivante
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      <figcaption style={{ ...styleParagraphe(variante), color: COULEURS.texteSecondaire }}>{titre}</figcaption>
    </figure>
  );
}
